import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Mail, Copy, Eye, Download } from 'lucide-react';

const AdminExpiryAlertsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCount, setSelectedCount] = useState(0);
  const [fetchedData, setFetchedData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiService.request('/admin/expiry-alerts', 'GET');
        if (response.success && response.data) {
          setFetchedData(response.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const expiryData = fetchedData.map((d: any) => {
    const providerName = d.provider_name || d.company_name || '—';
    const initials = providerName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || '??';
    const expiryDate = d.expiry_date || d.expires_at;
    const daysLeft = expiryDate
      ? Math.round((new Date(expiryDate).getTime() - Date.now()) / 86400000)
      : null;
    let status = 'ACTIVE';
    if (daysLeft !== null) {
      if (daysLeft < 0) status = 'EXPIRED';
      else if (daysLeft <= 7) status = 'CRITICAL';
      else if (daysLeft <= 30) status = 'URGENT';
    }
    return {
      id: d.id,
      provider: providerName,
      providerId: `ID: ${d.provider_id || d.id}`,
      avatar: initials,
      document: d.document_name || d.doc_name || d.document_type || '—',
      expiryDate: expiryDate ? new Date(expiryDate).toLocaleDateString('en-GB', { dateStyle: 'medium' }) : '—',
      daysLeft: daysLeft ?? 0,
      status,
      lastNotified: d.last_notified_at
        ? new Date(d.last_notified_at).toLocaleDateString('en-GB', { dateStyle: 'short' })
        : 'Never'
    };
  });

  const metrics = [
    {
      label: 'CRITICAL',
      value: String(expiryData.filter(e => e.status === 'CRITICAL').length),
      description: 'EXPIRING ≤ 7 DAYS',
      icon: '●',
      color: 'text-red-600'
    },
    {
      label: 'URGENT',
      value: String(expiryData.filter(e => e.status === 'URGENT').length),
      description: 'EXPIRING ≤ 30 DAYS',
      icon: '⚠️',
      color: 'text-orange-600'
    },
    {
      label: 'MONITORING',
      value: String(expiryData.filter(e => e.status === 'ACTIVE').length),
      description: 'ACTIVE / MONITORING',
      icon: '⏱️',
      color: 'text-blue-600'
    },
    {
      label: 'ACTION REQUIRED',
      value: String(expiryData.filter(e => e.status === 'EXPIRED').length),
      description: 'EXPIRED',
      icon: '❌',
      color: 'text-gray-900',
      bgColor: 'bg-gray-900 text-white'
    }
  ];

  const getStatusBadge = (status: string, daysLeft?: number) => {
    const config: Record<string, { bg: string; text: string }> = {
      EXPIRED: { bg: 'bg-red-100', text: 'text-red-700' },
      CRITICAL: { bg: 'bg-red-100', text: 'text-red-700' },
      URGENT: { bg: 'bg-orange-100', text: 'text-orange-700' },
      ACTIVE: { bg: 'bg-teal-100', text: 'text-teal-700' }
    };
    const c = config[status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
    return <Badge className={`${c.bg} ${c.text} border-0`}>● {status}</Badge>;
  };

  // Handler: Send reminders to all selected
  const handleSendReminders = async () => {
    if (selectedCount === 0) {
      alert('Please select alerts to send reminders');
      return;
    }

    try {
      const response = await apiService.request('/admin/expiry-alerts/send-reminders', 'POST', {
        count: selectedCount,
        sentBy: localStorage.getItem('adminId'),
        sentAt: new Date().toISOString()
      });
      
      if (response.success) {
        alert(`✓ Reminders sent to ${selectedCount} providers`);
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Send reminders error:', error);
      alert('Failed to send reminders: ' + error.message);
    }
  };

  // Handler: Send single reminder
  const handleSendSingleReminder = async (alertId: number) => {
    try {
      const response = await apiService.request(`/admin/expiry-alerts/${alertId}/send-reminder`, 'POST', {
        sentBy: localStorage.getItem('adminId'),
        sentAt: new Date().toISOString()
      });
      
      if (response.success) {
        alert('✓ Reminder sent successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Send single reminder error:', error);
      alert('Failed to send reminder: ' + error.message);
    }
  };

  // Handler: Copy alert template
  const handleCopyAlert = async (alertId: number) => {
    try {
      const response = await apiService.request(`/admin/expiry-alerts/${alertId}/template`, 'GET');
      
      if (response.success && response.data?.template) {
        await navigator.clipboard.writeText(response.data.template);
        alert('✓ Template copied to clipboard');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Copy alert error:', error);
      alert('Failed to copy template: ' + error.message);
    }
  };

  // Handler: View alert details
  const handleViewAlert = async (alertId: number) => {
    try {
      const response = await apiService.request(`/admin/expiry-alerts/${alertId}/details`, 'GET');
      
      if (response.success && response.data) {
        alert(`✓ Alert Details:\n\n${JSON.stringify(response.data, null, 2)}`);
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('View alert error:', error);
      alert('Failed to fetch alert details: ' + error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Expiry Alerts - VireWire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-8 py-8">
              <div className="mb-2 text-sm text-gray-600">
                <span>COMPLIANCE MANAGEMENT</span>
              </div>
              <h1 className="text-4xl font-bold text-gray-900">Expiry Alerts</h1>
            </div>
          </div>

          {/* Metrics */}
          <div className="max-w-7xl mx-auto px-8 py-8">
            <div className="grid grid-cols-4 gap-6 mb-8">
              {metrics.map((metric, idx) => (
                <Card key={idx} className={metric.bgColor ? metric.bgColor : ''}>
                  <CardContent className="pt-6">
                    <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${metric.bgColor ? 'text-white' : 'text-gray-600'}`}>
                      {metric.label}
                    </p>
                    <p className={`text-3xl font-bold mb-2 ${metric.color}`}>{metric.value}</p>
                    <p className={`text-xs ${metric.bgColor ? 'text-gray-200' : 'text-gray-600'}`}>{metric.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Selection Bar */}
            <div className="mb-6 flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked className="w-5 h-5 border-gray-300 rounded cursor-pointer" />
                <p className="font-bold text-gray-900">{selectedCount} Providers selected</p>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 text-sm">
                <Mail className="w-4 h-4" />
                SEND REMINDERS TO SELECTED
              </button>
            </div>

            {/* Expiry Alerts Table */}
            <Card>
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-6 py-4 text-left">
                          <input type="checkbox" className="w-5 h-5 border-gray-300 rounded cursor-pointer" />
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Provider</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Document</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Expiry Date</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Days Left</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Last Notified</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expiryData.length === 0 && (
                        <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">No expiry alerts found.</td></tr>
                      )}
                      {expiryData.map((item) => (
                        <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <input type="checkbox" checked className="w-5 h-5 border-gray-300 rounded cursor-pointer" />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold text-sm">{item.avatar}</div>
                              <div>
                                <p className="font-bold text-gray-900">{item.provider}</p>
                                <p className="text-xs text-gray-600">{item.providerId}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.document}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.expiryDate}</td>
                          <td className="px-6 py-4">
                            <p className={`font-bold text-sm ${item.daysLeft < 0 ? 'text-red-600' : item.daysLeft < 30 ? 'text-orange-600' : 'text-gray-900'}`}>
                              {item.daysLeft < 0 ? item.daysLeft : '+' + item.daysLeft}
                            </p>
                          </td>
                          <td className="px-6 py-4">{getStatusBadge(item.status, item.daysLeft)}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{item.lastNotified}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                <Mail className="w-4 h-4" />
                              </button>
                              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                <Copy className="w-4 h-4" />
                              </button>
                              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-600 uppercase tracking-widest font-bold">SHOWING {expiryData.length} ENTRIES</p>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1 text-sm font-bold text-gray-600 border border-gray-300 rounded hover:bg-gray-100">Previous</button>
                    <button className="w-8 h-8 bg-teal-600 text-white text-sm font-bold rounded">1</button>
                    <button className="w-8 h-8 border border-gray-300 text-sm font-bold rounded hover:bg-gray-100">2</button>
                    <button className="px-3 py-1 text-sm font-bold text-gray-600 border border-gray-300 rounded hover:bg-gray-100">Next</button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminExpiryAlertsPage;
